import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Home,
  FileText,
  Users,
  ChevronRight,
  X,
  Settings,
  Database,
  MessageCircle,
  CreditCard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../../services/common/authService";
import OrganizationService from "../../services/organization/OrganizationService";
import QuotaBadge from "../common/QuotaBadge";
import { baseURL } from "../../services/common/endPoints";

// Menu structure (labels/urls) for sidebar and route meta. Single source for page titles.
const baseMenuData = [
  { id: "dashboard", label: "Dashboard", url: "/dashboard" },
  {
    id: "export",
    label: "Export documentation",
    submenu: [
      { id: "products", label: "Products", url: "/products" },
      { id: "quotation-documents", label: "Quotation Documents", url: "/quotation-documents" },
      { id: "performa-documents", label: "Performa Documents", url: "/performa-documents" },
      { id: "invoice-documents", label: "Invoice Documents", url: "/invoice-documents" },
      { id: "bl-draft-documents", label: "BL Draft Documents", url: "/bl-draft-documents" },
      { id: "vgm-documents", label: "VGM Documents", url: "/vgm-documents" },
      { id: "annexure-documents", label: "Annexure Documents", url: "/annexure-documents" },
    ],
  },
  {
    id: "master",
    label: "Master Data",
    submenu: [
      { id: "units", label: "Units", url: "/units" },
      { id: "country", label: "Country", url: "/country" },
      { id: "containerType", label: "Container Type", url: "/containerType" },
      { id: "states", label: "States", url: "/states" },
      { id: "currency", label: "Currency", url: "/currency" },
      { id: "packageType", label: "Package Type", url: "/packageType" },
      { id: "city", label: "City", url: "/city" },
      { id: "paymentTerms", label: "Payment Terms", url: "/paymentTerms" },
      { id: "ports", label: "Ports", url: "/ports" },
      { id: "customers", label: "Customers", url: "/customers" },
    ],
  },
  { id: "billing", label: "Plan & Billing", url: "/billing" },
];

const publicRouteMap = {
  "/": "Home",
  "/login": "Login",
  "/signup": "Sign Up",
  "/notfound": "Page Not Found",
};

function buildRouteLabelMap() {
  const map = { ...publicRouteMap };
  baseMenuData.forEach((item) => {
    if (item.url) map[item.url] = item.label;
    if (item.submenu) item.submenu.forEach((sub) => { map[sub.url] = sub.label; });
  });
  map["/organization"] = "Organization Info";
  map["/employees"] = "Employees";
  map["/support"] = "Support";
  map["/billing"] = "Plan & Billing";
  return map;
}

const routeLabelMap = buildRouteLabelMap();
const pathPrefixes = Object.keys(routeLabelMap)
  .filter((p) => p !== "/")
  .sort((a, b) => b.length - a.length);

/**
 * Get page title label for pathname. Used by PageMeta for document title and meta tags.
 */
export function getRouteLabel(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";
  if (routeLabelMap[path]) return routeLabelMap[path];
  for (const prefix of pathPrefixes) {
    if (path === prefix || path.startsWith(prefix + "/")) return routeLabelMap[prefix];
  }
  return routeLabelMap["/"] || "Home";
}

const iconById = {
  dashboard: <Home className="h-4 w-4" />,
  export: <FileText className="h-4 w-4" />,
  master: <Database className="h-4 w-4" />,
  billing: <CreditCard className="h-4 w-4" />,
  employees: <Users className="h-4 w-4" />,
  support: <MessageCircle className="h-4 w-4" />,
};

const Sidebar = ({
  activeMenu,
  setActiveMenu,
  isSidebarOpen,
  setIsSidebarOpen,
  isCollapsed,
  setIsCollapsed,
  onLogout,
}) => {
  const [collapsedItems, setCollapsedItems] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive activeMenu key from current path (e.g. /country -> "master/country")
  const getActiveMenuFromPath = useCallback((pathname) => {
    const path = pathname.replace(/\/$/, "") || "/";
    for (const item of baseMenuData) {
      if (item.url && path === item.url) return item.id;
      if (item.submenu) {
        for (const sub of item.submenu) {
          if (sub.url && path === sub.url) return `${item.id}/${sub.id}`;
        }
      }
    }
    if (path === "/employees") return "employees";
    if (path === "/support") return "support";
    return null;
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("activeMenu");
    if (saved) setActiveMenu(saved);

    // Check if user is admin
    setIsAdmin(authService.isAdmin());

    // Fetch organization logo/document
    fetchOrganizationLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync active menu and expand parent when URL changes (direct nav or refresh)
  useEffect(() => {
    const key = getActiveMenuFromPath(location.pathname);
    if (key) {
      setActiveMenu(key);
      localStorage.setItem("activeMenu", key);
      const parentId = key.includes("/") ? key.split("/")[0] : null;
      if (parentId) {
        setCollapsedItems((p) => ({ ...p, [parentId]: true }));
      }
    }
  }, [location.pathname, getActiveMenuFromPath, setActiveMenu]);

  const fetchOrganizationLogo = async () => {
    try {
      const response = await OrganizationService.getOrganization();
      const data = response.data?.data || {};

      // Prefer organization_document_url, fallback to logo_url
      if (data.organization_document_url) {
        setOrganizationLogo(`${baseURL}${data.organization_document_url}`);
      } else if (data.logo_url) {
        setOrganizationLogo(data.logo_url);
      }
    } catch (error) {
      console.error("Error fetching organization logo:", error);
      // Keep default logo on error
    }
  };

  useEffect(() => {
    if (isCollapsed) {
      setCollapsedItems({});
    }
  }, [isCollapsed]);

  // Build menu items based on user role - recalculates when isAdmin changes
  const getMenuItems = useCallback(() => {
    const baseMenuItems = baseMenuData.map((item) => ({
      ...item,
      icon: iconById[item.id],
      submenu: item.submenu ? [...item.submenu] : undefined,
    }));

    if (isAdmin) {
      baseMenuItems.push({
        id: "employees",
        label: "Employees",
        url: "/employees",
        icon: iconById.employees,
      });
    }

    baseMenuItems.push({
      id: "support",
      label: "Support",
      url: "/support",
      icon: iconById.support,
    });

    return baseMenuItems;
  }, [isAdmin]);

  // Use useMemo to recalculate menuItems whenever getMenuItems changes
  const menuItems = useMemo(() => getMenuItems(), [getMenuItems]);

  const toggleSubmenu = (id) => {
    // Auto expand if sidebar is collapsed
    if (isCollapsed) setIsCollapsed(false);

    setCollapsedItems((p) => ({ ...p, [id]: !p[id] }));
  };

  const handleMenuItemClick = (parentId, item) => {
    // Retain collapsed state if sidebar is collapsed
    const fullPath = parentId ? `${parentId}/${item.id}` : item.id;

    localStorage.setItem("activeMenu", fullPath);
    setActiveMenu(fullPath);
    navigate(item.url);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const renderMenuItem = (item, isSubmenu = false, parentId = null) => {
    const hasSubmenu = item.submenu?.length > 0;
    const isOpen = collapsedItems[item.id] === true && hasSubmenu;
    // activeMenu is stored as "parentId/itemId" for submenus, so compare with full path for subitems
    const activeKey = parentId ? `${parentId}/${item.id}` : item.id;
    const isParentOfActive = !isSubmenu && activeMenu && activeMenu.startsWith(`${item.id}/`);
    const isActive = activeMenu === activeKey || isParentOfActive;

    return (
      <div key={item.id} className="relative group">
        <button
          onClick={() =>
            hasSubmenu
              ? toggleSubmenu(item.id)
              : handleMenuItemClick(parentId, item)
          }
          className={`w-full flex items-center ${
            isCollapsed && !isSubmenu
              ? "justify-center px-2 py-2.5"
              : "justify-between px-3 py-2.5"
          } rounded-xl text-xs transition-all duration-200 ${
            !isSubmenu
              ? isActive
                ? "bg-amber-100/90 text-amber-950 border-l-4 border-amber-600 font-bold shadow-xs"
                : "text-zinc-700 font-semibold hover:bg-amber-50/90 hover:text-amber-950"
              : isActive
                ? "bg-amber-100 text-amber-950 font-extrabold"
                : "text-zinc-700 font-medium hover:bg-amber-50/90 hover:text-amber-950"
          }`}
        >
          <span className="flex items-center gap-2.5">
            {!isSubmenu && (
              <span className={`transition-colors ${isActive ? "text-amber-700 font-bold" : "text-zinc-500 group-hover:text-amber-700"}`}>
                {item.icon}
              </span>
            )}
            {!isSubmenu && !isCollapsed && (
              <span className="font-semibold tracking-tight">{item.label}</span>
            )}
            {isSubmenu && <span className="font-medium text-[13px]">{item.label}</span>}
          </span>

          {hasSubmenu && !isCollapsed && (
            <ChevronRight
              className={`h-3.5 w-3.5 transition-transform duration-200 ${
                isOpen ? "rotate-90 text-amber-700 font-bold" : "text-zinc-400 group-hover:text-amber-700"
              }`}
            />
          )}
        </button>

        {/* Standard expanded submenu */}
        {hasSubmenu && isOpen && !isCollapsed && (
          <div className="ml-4 pl-2.5 border-l-2 border-amber-300 my-1 space-y-1">
            {item.submenu.map((subitem) =>
              renderMenuItem(subitem, true, item.id)
            )}
          </div>
        )}

        {/* Consistent Warm Amber Hover Flyout for Collapsed Sidebar */}
        {isCollapsed && !isSubmenu && (
          hasSubmenu ? (
            <div className="hidden group-hover:block absolute left-full top-0 -ml-1 pl-3 w-64 z-50 animate-in fade-in duration-150">
              <div className="bg-white border border-amber-200/90 shadow-xl rounded-2xl p-2.5 font-sans">
                <div className="px-3 py-2 font-bold text-xs text-amber-950 uppercase tracking-wider border-b border-amber-100 mb-2 flex items-center justify-between">
                  <span>{item.label}</span>
                  <span className="text-[10px] bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full font-bold">
                    {item.submenu.length}
                  </span>
                </div>
                <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
                  {item.submenu.map((subitem) => {
                    const subActiveKey = `${item.id}/${subitem.id}`;
                    const isSubActive = activeMenu === subActiveKey;
                    return (
                      <button
                        key={subitem.id}
                        onClick={() => handleMenuItemClick(item.id, subitem)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs transition-all ${
                          isSubActive
                            ? "bg-amber-100 text-amber-950 border-l-4 border-amber-600 font-bold shadow-xs"
                            : "text-zinc-700 font-medium hover:bg-amber-50 hover:text-amber-950"
                        }`}
                      >
                        {subitem.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden group-hover:block absolute left-full top-1/2 -translate-y-1/2 -ml-1 pl-3 z-50 pointer-events-none">
              <div className="bg-white border border-amber-200/90 text-amber-950 px-3.5 py-2 text-xs font-semibold rounded-xl shadow-xl whitespace-nowrap">
                {item.label}
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <>
      {/* Overlay Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-xs md:hidden z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen bg-white border-r border-zinc-200/90 shadow-2xl z-50 w-64 md:hidden transition-transform duration-300 flex flex-col ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="relative flex items-center justify-between h-16 border-b border-zinc-100 px-4 flex-shrink-0">
          {/* Logo Centered */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={organizationLogo || "/CompanyLogo.png"}
              alt="Company Logo"
              className="h-11 max-h-12 object-contain"
              onError={(e) => {
                e.target.src = "/CompanyLogo.png";
              }}
            />
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-1.5 rounded-xl text-zinc-500 hover:bg-zinc-100 flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1 scrollbar-hide">
          {menuItems.map((i) => renderMenuItem(i))}
        </nav>
        <div className="flex-shrink-0 border-t border-zinc-200/80 p-3 space-y-2 bg-zinc-50/80">
          <div className="px-0.5">
            <QuotaBadge className="w-full justify-between shadow-xs" />
          </div>
          <button
            onClick={() => {
              setActiveMenu("organization");
              localStorage.setItem("activeMenu", "organization");
              navigate("/organization");
              if (window.innerWidth < 768) setIsSidebarOpen(false);
            }}
            className={`w-full flex items-center px-3 py-2.5 rounded-xl text-xs transition-all ${
              location.pathname === "/organization"
                ? "bg-amber-100/90 text-amber-950 font-bold border-l-4 border-amber-600"
                : "text-zinc-700 font-semibold hover:bg-amber-50/90"
            }`}
          >
            <Settings className="h-4 w-4 flex-shrink-0 text-amber-600" />
            <span className="ml-2.5 font-semibold">Settings</span>
          </button>

          <button
            onClick={() => {
              onLogout?.();
              if (window.innerWidth < 768) setIsSidebarOpen(false);
            }}
            className="w-full flex items-center px-3 py-2.5 rounded-xl text-xs text-rose-700 font-bold hover:bg-rose-50 transition-colors"
          >
            <LogOut className="h-4 w-4 flex-shrink-0 text-rose-600" />
            <span className="ml-2.5 font-bold">Logout</span>
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 h-screen bg-white border-r border-zinc-200/90 shadow-xs z-50 flex-col transition-all duration-300 ${
          isCollapsed ? "w-16 overflow-visible" : "w-64"
        }`}
      >
        {/* Header + Collapse Button */}
        <div
          className={`flex items-center h-16 border-b border-zinc-100 px-3 ${
            isCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!isCollapsed ? (
            <>
              {/* Logo Centered */}
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={organizationLogo || "/CompanyLogo.png"}
                  alt="Company Logo"
                  className="h-11 max-h-12 object-contain"
                  onError={(e) => {
                    e.target.src = "/CompanyLogo.png";
                  }}
                />
              </div>

              {/* Close Button when Sidebar is Open */}
              <button
                onClick={() => setIsCollapsed(true)}
                className="p-1.5 rounded-xl text-zinc-500 hover:bg-amber-500/10 hover:text-amber-900 transition-colors flex items-center justify-center flex-shrink-0"
                title="Collapse Sidebar"
              >
                <PanelLeftClose className="h-5 w-5" />
              </button>
            </>
          ) : (
            /* Open Button in place of Logo when Sidebar is Collapsed */
            <button
              onClick={() => setIsCollapsed(false)}
              className="p-2 rounded-xl text-zinc-500 hover:bg-amber-500/10 hover:text-amber-900 transition-colors flex items-center justify-center"
              title="Expand Sidebar"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 p-3 space-y-1 scrollbar-hide ${
            isCollapsed ? "overflow-visible" : "overflow-y-auto"
          }`}
        >
          {menuItems.map((i) => renderMenuItem(i))}
        </nav>
        <div className="flex-shrink-0 border-t border-zinc-200/80 p-2.5 space-y-1 bg-zinc-50/80">
          {!isCollapsed && (
            <div className="px-0.5 pb-1">
              <QuotaBadge className="w-full justify-between shadow-xs" />
            </div>
          )}
          <button
            onClick={() => {
              setActiveMenu("organization");
              localStorage.setItem("activeMenu", "organization");
              navigate("/organization");
            }}
            className={`w-full flex items-center rounded-xl text-xs transition-all duration-150 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            } ${
              location.pathname === "/organization"
                ? "bg-amber-100/90 text-amber-950 font-bold border-l-4 border-amber-600 shadow-xs"
                : "text-zinc-700 font-semibold hover:bg-amber-50/90 hover:text-amber-950"
            }`}
            title="Organization Info Settings"
          >
            <Settings className={`h-4 w-4 flex-shrink-0 ${location.pathname === "/organization" ? "text-amber-600" : "text-zinc-500"}`} />
            {!isCollapsed && <span className="ml-2.5 font-semibold">Settings</span>}
          </button>

          <button
            onClick={onLogout}
            className={`w-full flex items-center rounded-xl text-xs text-rose-700 font-bold hover:bg-rose-50 transition-colors ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
            title="Logout"
          >
            <LogOut className="h-4 w-4 flex-shrink-0 text-rose-600" />
            {!isCollapsed && <span className="ml-2.5 font-bold">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
